import {
  HierarchicalLayout,
  HierarchicalLayoutConfig,
  HierarchicalLayoutEdgeDescriptor,
  HierarchicalLayoutRoutingStyle,
  Insets,
  LayoutGraph,
  LayoutGraphGrouping,
  LayoutKeys,
  LayoutNode,
  LayoutOrientationStringValues,
  OrthogonalLayout,
  OrthogonalLayoutConfig,
  OrthogonalLayoutEdgeDescriptor,
  RecursiveGroupLayout,
  SeriesParallelLayout,
  SeriesParallelLayoutConfig,
  TabularLayout,
  TabularLayoutConfig,
  TimeSpan,
} from '@ardoq/yfiles';
import { isTruthy } from '@ardoq/common-helpers';
import { sortedIndexOf } from 'lodash';
import {
  BlocksGraph,
  BlocksLayoutStyle,
  BlocksLayoutType,
  BlocksNode,
  NodeLayoutData,
} from '../types';
import { isGroup } from '../util';
import { buildOrdinates } from './util';

const maximumMarkerWidth = 16;

const hierarchicLayoutOptions: HierarchicalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  minimumLayerDistance: 35,
  groupLayeringPolicy: 'recursive',
  fromScratchLayeringStrategy: 'hierarchical-optimal',
  defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
    backLoopRouting: false,
    routingStyleDescriptor: {
      defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
    },
    minimumFirstSegmentLength: maximumMarkerWidth,
    minimumLastSegmentLength: maximumMarkerWidth,
    minimumLength: 2 * maximumMarkerWidth,
  }),
  stopDuration: TimeSpan.fromMilliseconds(10000),
};

const orthogonalLayoutOptions: OrthogonalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  defaultEdgeDescriptor: new OrthogonalLayoutEdgeDescriptor({}),
  stopDuration: TimeSpan.fromMilliseconds(10000),
};

const seriesparallelLayoutOptions: SeriesParallelLayoutConfig = {};

/* `TabularLayout` takes no orientation: a table has no flow direction to reverse, so `grid`
   is the one layout type the orientation control does not reach. `considerEdges` would sort
   the cells by an edge-length heuristic instead of the order the nodes arrive in. */
const gridLayoutOptions: TabularLayoutConfig = {
  layoutMode: 'auto-size',
  considerEdges: false,
  nodeLabelPlacement: 'consider',
};

function* leaves(node: BlocksNode): Iterable<BlocksNode> {
  if (node.children) {
    for (const child of node.children) {
      yield* leaves(child);
    }
  } else {
    yield node;
  }
}

function* groups(node: BlocksNode): Iterable<BlocksNode> {
  if (node.children) {
    yield node;

    for (const child of node.children) {
      yield* groups(child);
    }
  }
}

/**
 * Whether anything nests. Checking the root's own children is enough: a group at any depth
 * has an ancestor that is itself a direct child of the root.
 */
const hasGroups = (graph: BlocksGraph): boolean =>
  (graph.root.children ?? []).some(child => child.children !== null);

/**
 * Whether this group's children still have to be laid out.
 *
 * The flag alone is not enough, because the producer of a request does not control it.
 * `mergeGraph` restores a reused group's flag from the node it replaced - false, since last frame's
 * fixup cleared it - and re-dirties parents only for the nodes in `createStableNodeMap`, which
 * skips everything with a null `modelId`. Field-value and "all" grouping build exactly those, so a
 * component-backed group whose children are all field-value groups is left clean and the pass that
 * would read their `requestedCell` never runs.
 *
 * A request no layout has applied yet therefore counts as dirty in its own right. Only an
 * unapplied one: a child that already carries `layoutData` is left alone, so this does not reach
 * behind a reader who has just dragged that child by hand.
 */
const hasDirtyChildLayouts = (group: BlocksNode): boolean =>
  group.isLayoutDirty === true || // force booleany to boolean
  (isGroup(group) &&
    group.children.some(
      child => Boolean(child.requestedCell) && !child.layoutData
    ));

/**
 * Two tracks, because a group starts on an odd one: a single track of margin would put its first
 * child on an even track, which is a routing channel. The same margin the Assistant Blocks
 * compiler leaves, restated rather than imported - it is a property of this grid, and the
 * dependency between the two only runs the other way.
 */
const GROUP_MARGIN = 2;

/**
 * A group's cell, widened until it holds the cells its children were given.
 *
 * The second rule of the one grid is that a group's span encloses its descendants, and a request
 * cannot be relied on for it: the caller compiled that span against the children it placed, so
 * anything the fill below adds is past the end of it. A group holding one 1x1 child is five tracks
 * across, which leaves room for exactly one filler row - the second and later ones landed outside
 * the group, in whatever occupies the tracks beneath it.
 *
 * So the request is a floor rather than the answer, which is how the compiler's own `spanOf`
 * already treats an authored span. Its sizing is a fixed point here: a group that already encloses
 * its children is left exactly as it asked.
 */
const grownToEnclose = (
  cell: NodeLayoutData,
  children: BlocksNode[]
): NodeLayoutData => {
  let lastCol = cell.col + cell.colSpan - 1;
  let lastRow = cell.row + cell.rowSpan - 1;

  for (const child of children) {
    const inner = child.layoutData;

    if (inner) {
      lastCol = Math.max(lastCol, inner.col + inner.colSpan - 1 + GROUP_MARGIN);
      lastRow = Math.max(lastRow, inner.row + inner.rowSpan - 1 + GROUP_MARGIN);
    }
  }

  return {
    ...cell,
    colSpan: lastCol - cell.col + 1,
    rowSpan: lastRow - cell.row + 1,
  };
};

/**
 * Every child group widened to hold its own children.
 *
 * Idempotent, because it has to run on both sides of the gap-fill below. A group whose cell the
 * fill supplies has no cell while the fill is being sized, so the pass that runs first cannot
 * reach it - and a 1x1 filler cell is the one thing a group holding anything cannot be.
 */
const growGroupsToEnclose = (children: BlocksNode[]): void => {
  for (const child of children) {
    if (isGroup(child) && child.layoutData) {
      child.layoutData = grownToEnclose(child.layoutData, child.children);
    }
  }
};

const fixupLocalManualLayout = (group: BlocksNode) => {
  if (isGroup(group)) {
    /* The caller's cells first, because they are inputs: whatever `requestedCell` says is what the
       grid gets sized against, rather than whatever last frame's layout happened to leave behind.
       This is the whole reason the field exists - the merge clears `layoutData` on a reused node so
       a position cannot outlive the content that justified it, and before there was somewhere to
       put a request, that clearing took the request with it. */
    for (const child of group.children) {
      if (child.requestedCell) {
        child.layoutData = { ...child.requestedCell };
      }
    }

    /* After the requests are in and before the counts below, so a group that had to grow is
       measured at the size it grew to and the fill lands clear of all of it. `doit` walks children
       before their parent, so by the time a group is placed here every one of its own children
       already has a cell. */
    growGroupsToEnclose(group.children);

    /* get the group's children's grid size */

    let col_cnt = 0;
    let row_cnt = 0;

    for (const child of group.children) {
      const layoutData = child.layoutData;

      if (layoutData) {
        col_cnt = Math.max(layoutData.col + layoutData.colSpan, col_cnt);
        row_cnt = Math.max(layoutData.row + layoutData.rowSpan, row_cnt);
      }
    }

    /* define layout for any unlayed out children */

    const unlayedOut = group.children.filter(child => !child.layoutData);

    /* Three defects lived in the arithmetic below, and all of them are reachable the moment a
       caller supplies cells for some children and not others - which is the case this field is
       for.

       `col_cnt` is 0 when nothing carries a cell, and `index % 0` is `NaN`, which reaches
       `resetGridArrays` as an array length and throws. The old `index % col_cnt` numbered from 0,
       putting children on even tracks - the routing channels - where `fixupLocalGridLayout`
       immediately below has always used the odd ones. And numbering from track 1 is the grid's
       origin, not this group's: `requestedCell` carries cells in the one global grid, so a group
       anywhere but the top left had its unpositioned children placed outside itself, in whichever
       group owns the left of the diagram. Rows never had that fault, because `row_cnt` is measured
       off the siblings; columns now are too.

       With nothing placed, the origin is the group's own content area. Its `layoutData` is not
       written yet - `fixupLayout` walks children before their parent - but its `requestedCell` is,
       since the caller writes that before layout runs. */
    const placed = group.children
      .map(child => child.layoutData)
      .filter(isTruthy);

    /* `reduce` rather than `Math.min(...cols)`: this runs over whichever group a reader drags in
       too (`interactions/edit.ts` sets `layoutType = 'manual'` on it), so the argument count is
       the number of children in a group and a flat root in a large workspace is a real size. */
    const request = group.requestedCell;
    const colOrigin =
      placed.length > 0
        ? placed.reduce(
            (leftmost, cell) => Math.min(leftmost, cell.col),
            Number.POSITIVE_INFINITY
          )
        : request
          ? request.col + GROUP_MARGIN
          : 1;
    const rowOrigin =
      placed.length > 0
        ? row_cnt + 1
        : request
          ? request.row + GROUP_MARGIN
          : 1;
    const colTracks = placed.length > 0 ? col_cnt - colOrigin : 0;
    const columns = Math.max(
      1,
      colTracks > 0
        ? Math.ceil(colTracks / 2)
        : Math.ceil(Math.sqrt(unlayedOut.length))
    );

    unlayedOut.forEach((child, index) => {
      child.layoutData = {
        col: colOrigin + 2 * (index % columns),
        colSpan: 1,
        row: rowOrigin + 2 * Math.floor(index / columns),
        rowSpan: 1,
      };
    });

    /* Again, for the groups the fill just placed. An unplaced child can be a group, and post-order
       `doit` means its own children already carry cells - so the 1x1 the fill gives it is a box
       nowhere near what it contains. That is the defect this whole change exists to remove, in the
       one corner the pass above cannot see. */
    growGroupsToEnclose(unlayedOut);
  }

  group.isLayoutDirty = false;
};

const fixupLocalGridLayout = (group: BlocksNode) => {
  if (group.children) {
    const length = group.children.length;
    const col_cnt = Math.ceil(Math.sqrt(length / Math.SQRT2));

    for (let i = 0; i < length; ++i) {
      const child = group.children[i];

      child.layoutData = {
        col: 1 + 2 * (i % col_cnt),
        colSpan: 1,
        row: 1 + 2 * Math.floor(i / col_cnt),
        rowSpan: 1,
      };
    }
  }

  group.isLayoutDirty = false;
};

const fixupLocalYFilesLayout = (
  _group: BlocksNode,
  _layoutType: BlocksLayoutType
) => {
  /* THIS IS A STUB
  const yLayout =
    layoutType === 'process'
      ? new HierarchicalLayout({ ...hierarchicLayoutOptions })
      : layoutType === 'compact'
        ? new OrthogonalLayout({ ...orthogonalLayoutOptions })
        : layoutType === 'seriesparallel'
          ? new SeriesParallelLayout({ ...seriesparallelLayoutOptions })
          : null;

  const siblingAscendant = (u: BlocksNode, v: BlocksNode) => {
    let ret: BlocksNode | null = v;

    while (ret && ret.parent !== u.parent) {
      ret = ret.parent;
    }

    return ret;
  };

  if (!group.children || !yLayout) {
    return;
  }

  /* prepare for local yfiles place and route

  const yNodes = new Map<BlocksNode, LayoutNode>();
  const nodes = new Map<LayoutNode, BlocksNode>();
  const yGraph = new LayoutGraph();

  // get the ynodes for the group children only

  const buildYNodes = (group: BlocksNode) => {
    for (const node of leaves(group)) {
      const yNode = yGraph.createNode();

      //yNode.layout.size = new Size(100, 100);
      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }

    const grouping = LayoutGraphGrouping.replaceGrouping(yGraph);

    for (const node of groups(group)) {
      //const cnt = getDescendantCount(node);
      //const sz = Math.ceil(Math.sqrt(cnt));

      const yNode = yGraph.createNode();
      //yNode.layout.size = new Size(100 * sz, 100 * sz);
      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);
      const yParent = node && node.parent ? yNodes.get(node.parent) : null;

      if (node && yParent) {
        grouping.setParent(ynode, yParent);
      }
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);

      if (node && node.children) {
        grouping.setIsGroupNode(ynode, true);
      }
    }
  };

  // get the relevant ylinks only

  const buildYLinks = (node: BlocksNode) => {
    /*
                for(const child of group.children) {
            if(child.links) {
                for(const link of child.links) {
                    const source = siblingAscendant(child, link.source); // starting from link.source move upwards looking for a node whos parent is group
                    const target = siblingAscendant(child, link.target); // starting from link.target move upwards looking for a node whos parent is group

                    if(source && target) {
                        // create a ylink from source to target
                    }
                }
            }
        }

    if (node.links) {
      for (const link of node.links) {
        const ySource = link.source === node ? yNodes.get(link.source) : null;
        const yTarget = ySource ? yNodes.get(link.target) : null;

        if (ySource && yTarget) {
          yGraph.createEdge(ySource, yTarget);
        }
      }
    }

    if (node.children) {
      for (const child of node.children) {
        buildYLinks(child);
      }
    }
  };

  function* ychildren(group: BlocksNode) {
    if (group.children) {
      for (const child of group.children) {
        yield yNodes.get(child)!;
      }
    }
  }

  buildYNodes(group);
  buildYLinks(group);
  yLayout.applyLayout(yGraph);

  // ordinals, extract etc

  */
};

const fixupGlobalLayout = (
  graph: BlocksGraph,
  layoutStyle: BlocksLayoutStyle
) => {
  const root = graph.root;
  const layoutType = layoutStyle.type;
  const orientation = layoutStyle.orientation;
  const yLayoutOrientation: LayoutOrientationStringValues = orientation;

  const yLayout =
    layoutType === 'process'
      ? new HierarchicalLayout({
          ...hierarchicLayoutOptions,
          layoutOrientation: yLayoutOrientation,
        })
      : layoutType === 'compact'
        ? new OrthogonalLayout({
            ...orthogonalLayoutOptions,
            layoutOrientation: yLayoutOrientation,
          })
        : layoutType === 'seriesparallel'
          ? new RecursiveGroupLayout({
              coreLayout: new SeriesParallelLayout({
                ...seriesparallelLayoutOptions,
                layoutOrientation: yLayoutOrientation,
              }),
            })
          : layoutType === 'grid' && hasGroups(graph)
            ? new RecursiveGroupLayout({
                coreLayout: new TabularLayout({ ...gridLayoutOptions }),
              })
            : null;

  if (!root || !yLayout) {
    return;
  }

  /* prepare for global yfiles place and route */

  const yNodes = new Map<BlocksNode, LayoutNode>();
  const nodes = new Map<LayoutNode, BlocksNode>();
  const yGraph = new LayoutGraph();

  const buildYNodes = (group: BlocksNode) => {
    for (const node of leaves(group)) {
      const yNode = yGraph.createNode();

      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }

    const grouping = LayoutGraphGrouping.replaceGrouping(yGraph);

    for (const node of groups(group)) {
      const yNode = yGraph.createNode();

      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);
      const yParent = node && node.parent ? yNodes.get(node.parent) : null;

      if (node && yParent) {
        grouping.setParent(ynode, yParent);
      }
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);

      if (node && node.children) {
        grouping.setIsGroupNode(ynode, true);
      }
    }
  };

  const buildYLinks = (node: BlocksNode) => {
    if (node.links) {
      for (const link of node.links) {
        const ySource = link.source === node ? yNodes.get(link.source) : null;
        const yTarget = ySource ? yNodes.get(link.target) : null;

        if (ySource && yTarget) {
          yGraph.createEdge(ySource, yTarget);
        }
      }
    }

    if (node.children) {
      for (const child of node.children) {
        buildYLinks(child);
      }
    }
  };

  buildYNodes(root);
  buildYLinks(root);

  yGraph.context.addItemConstantData(
    LayoutKeys.GROUP_NODE_PADDING_DATA_KEY,
    new Insets(1, 1, 1, 1)
  );

  yGraph.applyLayout(yLayout);

  const epsilon = 0.1;
  const leftEdge = (ynode: LayoutNode) => ynode.layout.x;
  const topEdge = (ynode: LayoutNode) => ynode.layout.y;
  const rightEdge = (ynode: LayoutNode) =>
    ynode.layout.x + ynode.layout.width - epsilon;
  const bottomEdge = (ynode: LayoutNode) =>
    ynode.layout.y + ynode.layout.height - epsilon;

  const cols = buildOrdinates(yNodes.values(), leftEdge, rightEdge);
  const rows = buildOrdinates(yNodes.values(), topEdge, bottomEdge);

  for (const [node, ynode] of yNodes) {
    const col = sortedIndexOf(cols, leftEdge(ynode));
    const row = sortedIndexOf(rows, topEdge(ynode));
    node.layoutData = {
      col,
      row,
      colSpan: sortedIndexOf(cols, rightEdge(ynode)) - col,
      rowSpan: sortedIndexOf(rows, bottomEdge(ynode)) - row,
    };
  }
};

/* this function makes sure that all the nodes in the graph have valid layout data */
export const fixupLayout = (
  graph: BlocksGraph,
  layoutStyle: BlocksLayoutStyle
): void => {
  let doGlobalLayout = false;

  /* try to fix all the dirty groups using local layout */

  const doit = (group: BlocksNode) => {
    if (group.children) {
      for (const child of group.children) {
        doit(child);
      }

      if (hasDirtyChildLayouts(group)) {
        switch (group.layoutType) {
          case undefined:
            if (layoutStyle.type === 'grid' && !hasGroups(graph)) {
              fixupLocalGridLayout(group);
            }
            break;

          case 'manual':
            fixupLocalManualLayout(group);
            break;
          case 'grid':
            fixupLocalGridLayout(group);
            break;
          case 'process':
            fixupLocalYFilesLayout(group, group.layoutType);
            break;
          case 'compact':
            fixupLocalYFilesLayout(group, group.layoutType);
            break;
        }

        doGlobalLayout = doGlobalLayout || hasDirtyChildLayouts(group);
      }
    }
  };

  doit(graph.root);

  if (doGlobalLayout) {
    /* not all dirty groups were fixed using local layout; try global layout */
    fixupGlobalLayout(graph, layoutStyle);
  }
  /*
  if(graph.root) {
    verifyLayout(graph);
  }
*/
};

export const resetLayout = (
  graph: BlocksGraph,
  _layoutStyle: BlocksLayoutStyle
) => {
  const doit = (group: BlocksNode) => {
    if (isGroup(group)) {
      for (const child of group.children) {
        doit(child);
      }

      group.layoutType = undefined;
      group.isLayoutDirty = true;
    }
  };

  doit(graph.root);
};
